import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, TrainingDayExercise, Exercise, UniversalWeightType } from '../database';
import SetEditor from '../components/SetEditor';
import ExerciseSelector from '../components/ExerciseSelector';
import { getSuggestedWeight, isSignificantWeightDifference, calculateUniversalWeight, formatUniversalWeightType } from '../utils/weightCalculation';

const TrainingDayForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = id !== 'new';

  const [name, setName] = useState('');
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [selectedExercises, setSelectedExercises] = useState<TrainingDayExercise[]>([]);
  const [exerciseCache, setExerciseCache] = useState<Map<number, Exercise>>(new Map());
  const [showExerciseSelector, setShowExerciseSelector] = useState(false);
  const [showUniversalWeightModal, setShowUniversalWeightModal] = useState<{
    exerciseId: number;
    exerciseName: string;
    suggestedWeight: number;
    targetReps: number;
  } | null>(null);
  const [universalWeightInput, setUniversalWeightInput] = useState<number>(0);
  const [universalWeightType, setUniversalWeightType] = useState<UniversalWeightType>('5RM');

  const loadTrainingDay = useCallback(async () => {
    if (!isEditing) return; // Don't try to load for new training days
    
    try {
      const dayId = parseInt(id!);
      if (isNaN(dayId)) return; // Invalid ID
      
      const day = await db.trainingDays.get(dayId);
      if (day) {
        setName(day.name);
        const dayExercises = await db.trainingDayExercises
          .where('trainingDayId')
          .equals(day.id!)
          .sortBy('order');
        setSelectedExercises(dayExercises);
      }
    } catch (error) {
      console.error('Failed to load training day:', error);
    }
  }, [isEditing, id]);

  useEffect(() => {
    loadExercises();
    if (isEditing) {
      loadTrainingDay();
    }
  }, [id, isEditing, loadTrainingDay]);

  const loadExercises = async () => {
    try {
      const allExercises = await db.exercises.orderBy('name').toArray();
      setExercises(allExercises);
      
      // Update exercise cache
      const cache = new Map<number, Exercise>();
      allExercises.forEach(exercise => {
        if (exercise.id) {
          cache.set(exercise.id, exercise);
        }
      });
      setExerciseCache(cache);
    } catch (error) {
      console.error('Failed to load exercises:', error);
    }
  };

  const addExercise = async (exercise: Exercise) => {
    const defaultReps = 10;
    const suggestedWeight = getSuggestedWeight(
      exercise.universalWeight,
      exercise.universalWeightType,
      defaultReps
    );
    
    const newTrainingDayExercise: TrainingDayExercise = {
      trainingDayId: isEditing ? parseInt(id!) : 0, // Will be updated when saving
      exerciseId: exercise.id!,
      weight: suggestedWeight || 0,
      sets: [
        { targetReps: defaultReps, order: 0 },
        { targetReps: defaultReps, order: 1 },
        { targetReps: defaultReps, order: 2 }
      ],
      order: selectedExercises.length,
      discoveryMode: !exercise.universalWeight // Default to discovery if no universal weight
    };
    
    // Add to cache immediately so getExerciseName works right away
    if (exercise.id) {
      setExerciseCache(prev => new Map(prev.set(exercise.id!, exercise)));
    }
    
    const updatedExercises = [...selectedExercises, newTrainingDayExercise];
    setSelectedExercises(updatedExercises);
    
    // Scroll to the newly added exercise after state updates
    setTimeout(() => {
      const exerciseElements = document.querySelectorAll('[data-exercise-index]');
      const newExerciseElement = exerciseElements[updatedExercises.length - 1];
      if (newExerciseElement) {
        newExerciseElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
    
    // If no universal weight exists, prompt user to set one
    if (!exercise.universalWeight && exercise.id) {
      setShowUniversalWeightModal({
        exerciseId: exercise.id,
        exerciseName: exercise.name,
        suggestedWeight: 0,
        targetReps: defaultReps
      });
      setUniversalWeightInput(0);
      setUniversalWeightType('5RM');
    }
    
    // Refresh exercises list to include any newly created exercises
    await loadExercises();
    
    setShowExerciseSelector(false);
  };

  const updateExercise = (index: number, updates: Partial<TrainingDayExercise>) => {
    const updated = selectedExercises.map((ex, i) =>
      i === index ? { ...ex, ...updates } : ex
    );
    
    // Auto-update weight when sets change (based on final set target reps) - only if not in discovery mode
    if (updates.sets && !updates.weight && !updated[index].discoveryMode) {
      const exercise = updated[index];
      const exerciseData = exerciseCache.get(exercise.exerciseId);
      
      if (exerciseData?.universalWeight && updates.sets.length > 0) {
        const finalSetReps = updates.sets[updates.sets.length - 1].targetReps;
        const suggestedWeight = getSuggestedWeight(
          exerciseData.universalWeight,
          exerciseData.universalWeightType,
          finalSetReps
        );
        
        if (suggestedWeight) {
          updated[index].weight = suggestedWeight;
        }
      }
    }
    
    // When toggling discovery mode, handle weight changes
    if (updates.discoveryMode !== undefined) {
      const exercise = updated[index];
      const exerciseData = exerciseCache.get(exercise.exerciseId);
      
      if (updates.discoveryMode) {
        // Switching to discovery mode - set weight to 0
        updated[index].weight = 0;
      } else if (exerciseData?.universalWeight && exercise.sets.length > 0) {
        // Switching from discovery mode - calculate suggested weight
        const finalSetReps = exercise.sets[exercise.sets.length - 1].targetReps;
        const suggestedWeight = getSuggestedWeight(
          exerciseData.universalWeight,
          exerciseData.universalWeightType,
          finalSetReps
        );
        if (suggestedWeight) {
          updated[index].weight = suggestedWeight;
        }
      }
    }
    
    setSelectedExercises(updated);
    
    // Check if weight was manually changed and suggest updating universal weight
    if (updates.weight !== undefined) {
      const exercise = updated[index];
      const exerciseData = exerciseCache.get(exercise.exerciseId);
      
      if (exerciseData?.universalWeight && exercise.sets.length > 0) {
        const finalSetReps = exercise.sets[exercise.sets.length - 1].targetReps;
        const suggestedWeight = getSuggestedWeight(
          exerciseData.universalWeight,
          exerciseData.universalWeightType,
          finalSetReps
        );
        
        if (suggestedWeight && isSignificantWeightDifference(suggestedWeight, updates.weight)) {
          const newUniversalWeight = calculateUniversalWeight(
            updates.weight,
            finalSetReps,
            exerciseData.universalWeightType || '5RM'
          );
          
          setShowUniversalWeightModal({
            exerciseId: exercise.exerciseId,
            exerciseName: exerciseData.name,
            suggestedWeight: Math.round(newUniversalWeight * 4) / 4,
            targetReps: finalSetReps
          });
          setUniversalWeightInput(Math.round(newUniversalWeight * 4) / 4);
          setUniversalWeightType(exerciseData.universalWeightType || '5RM');
        }
      }
    }
  };

  const removeExercise = (index: number) => {
    setSelectedExercises(selectedExercises.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    try {
      const now = new Date();
      let trainingDayId: number;

      if (isEditing) {
        const dayId = parseInt(id!);
        if (isNaN(dayId)) {
          throw new Error(`Invalid training day ID: ${id}`);
        }
        await db.trainingDays.update(dayId, {
          name: name.trim(),
          updatedAt: now
        });
        trainingDayId = dayId;
        await db.trainingDayExercises.where('trainingDayId').equals(trainingDayId).delete();
      } else {
        trainingDayId = await db.trainingDays.add({
          name: name.trim(),
          exercises: [],
          createdAt: now,
          updatedAt: now
        });
      }

      for (const exercise of selectedExercises) {
        await db.trainingDayExercises.add({
          ...exercise,
          trainingDayId
        });
      }

      navigate('/training-days');
    } catch (error) {
      console.error('Failed to save training day:', error);
    }
  };

  const getExerciseName = (exerciseId: number) => {
    // Try cache first (for immediately added exercises), then exercises array
    return exerciseCache.get(exerciseId)?.name || 
           exercises.find(ex => ex.id === exerciseId)?.name || 
           'Unknown';
  };
  
  const getExerciseData = (exerciseId: number): Exercise | undefined => {
    return exerciseCache.get(exerciseId) || exercises.find(ex => ex.id === exerciseId);
  };
  
  const saveUniversalWeight = async () => {
    if (!showUniversalWeightModal || universalWeightInput <= 0) return;
    
    try {
      await db.exercises.update(showUniversalWeightModal.exerciseId, {
        universalWeight: universalWeightInput,
        universalWeightType: universalWeightType,
        universalWeightUpdatedAt: new Date()
      });
      
      // Update cache
      const exerciseData = getExerciseData(showUniversalWeightModal.exerciseId);
      if (exerciseData) {
        const updatedExercise = {
          ...exerciseData,
          universalWeight: universalWeightInput,
          universalWeightType: universalWeightType,
          universalWeightUpdatedAt: new Date()
        };
        setExerciseCache(prev => new Map(prev.set(showUniversalWeightModal.exerciseId, updatedExercise)));
      }
      
      // Update suggested weights for all instances of this exercise
      const updatedSelectedExercises = selectedExercises.map(ex => {
        if (ex.exerciseId === showUniversalWeightModal.exerciseId && ex.sets.length > 0) {
          const finalSetReps = ex.sets[ex.sets.length - 1].targetReps;
          const suggestedWeight = getSuggestedWeight(
            universalWeightInput,
            universalWeightType,
            finalSetReps
          );
          return suggestedWeight ? { ...ex, weight: suggestedWeight } : ex;
        }
        return ex;
      });
      setSelectedExercises(updatedSelectedExercises);
      
      setShowUniversalWeightModal(null);
      await loadExercises(); // Refresh the exercises list
    } catch (error) {
      console.error('Failed to save universal weight:', error);
    }
  };

  const excludeIds = selectedExercises.map(sel => sel.exerciseId);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <button
          onClick={() => navigate('/training-days')}
          className="text-blue-600"
        >
          ← Back
        </button>
        <h2 className="text-xl font-bold text-gray-900">
          {isEditing ? 'Edit Training Day' : 'New Training Day'}
        </h2>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Training Day Name
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded"
          placeholder="e.g., Push Day A"
        />
      </div>

      <div>
        <div className="mb-3">
          <h3 className="font-medium text-gray-900">Exercises</h3>
        </div>

        {selectedExercises.length === 0 ? (
          <div className="text-center text-gray-500 py-4 border-2 border-dashed border-gray-300 rounded">
            <p>No exercises added yet</p>
          </div>
        ) : (
          <div className="space-y-6">
            {selectedExercises.map((exercise, index) => (
              <div key={index} data-exercise-index={index} className="bg-white border-2 border-blue-100 rounded-xl p-5 shadow-sm">
                {/* Exercise Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                    <h4 className="font-semibold text-gray-900 text-lg">
                      {getExerciseName(exercise.exerciseId)}
                    </h4>
                  </div>
                  <button
                    onClick={() => removeExercise(index)}
                    className="text-red-600 text-sm hover:text-red-800 font-medium"
                  >
                    Remove
                  </button>
                </div>

                {/* Weight and Universal Weight Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  {(() => {
                    const exerciseData = getExerciseData(exercise.exerciseId);
                    const finalSetReps = exercise.sets.length > 0 ? exercise.sets[exercise.sets.length - 1].targetReps : 10;
                    const suggestedWeight = exerciseData ? getSuggestedWeight(
                      exerciseData.universalWeight,
                      exerciseData.universalWeightType,
                      finalSetReps
                    ) : null;
                    const isAutoCalculated = suggestedWeight && !exercise.discoveryMode && Math.abs(exercise.weight - suggestedWeight) < 0.1;
                    
                    return (
                      <div>
                        {/* Discovery Mode Toggle */}
                        <div className="mb-4 flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Weight Setting
                            </div>
                            <div className="text-xs text-gray-600">
                              {exercise.discoveryMode ? 
                                'Will discover weight during workout' : 
                                'Using calculated/manual weight'
                              }
                            </div>
                          </div>
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={exercise.discoveryMode}
                              onChange={(e) => updateExercise(index, { discoveryMode: e.target.checked })}
                              className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">Discovery Mode</span>
                          </label>
                        </div>
                        
                        {/* Universal Weight Info - only show when not in discovery mode */}
                        {!exercise.discoveryMode && exerciseData?.universalWeight ? (
                          <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="text-sm font-medium text-blue-900">
                                  Your {formatUniversalWeightType(exerciseData.universalWeightType!)}: {exerciseData.universalWeight} lbs
                                </div>
                                <div className="text-xs text-blue-700">
                                  {exerciseData.universalWeightUpdatedAt ? 
                                    `Updated ${exerciseData.universalWeightUpdatedAt.toLocaleDateString()}` : 
                                    'Baseline weight for calculations'
                                  }
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setShowUniversalWeightModal({
                                    exerciseId: exercise.exerciseId,
                                    exerciseName: getExerciseName(exercise.exerciseId),
                                    suggestedWeight: exerciseData.universalWeight!,
                                    targetReps: finalSetReps
                                  });
                                  setUniversalWeightInput(exerciseData.universalWeight!);
                                  setUniversalWeightType(exerciseData.universalWeightType!);
                                }}
                                className="text-xs text-blue-600 hover:text-blue-800 underline"
                              >
                                Update
                              </button>
                            </div>
                          </div>
                        ) : !exercise.discoveryMode ? (
                          <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-yellow-800">
                                No baseline set - weights must be entered manually
                              </div>
                              <button
                                onClick={() => {
                                  setShowUniversalWeightModal({
                                    exerciseId: exercise.exerciseId,
                                    exerciseName: getExerciseName(exercise.exerciseId),
                                    suggestedWeight: 0,
                                    targetReps: finalSetReps
                                  });
                                  setUniversalWeightInput(0);
                                  setUniversalWeightType('5RM');
                                }}
                                className="text-xs text-yellow-700 hover:text-yellow-900 underline font-medium"
                              >
                                Set Baseline
                              </button>
                            </div>
                          </div>
                        ) : null}
                        
                        {/* Weight Input - only show when not in discovery mode */}
                        {!exercise.discoveryMode ? (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                              Weight for {finalSetReps} reps (lbs)
                            </label>
                            {isAutoCalculated && (
                              <span className="text-xs text-green-600 font-medium">
                                ✓ Auto-calculated
                              </span>
                            )}
                          </div>
                          
                          <div className="flex space-x-2">
                            <input
                              type="number"
                              value={exercise.weight || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '' || value === '0') {
                                  updateExercise(index, { weight: 0 });
                                } else {
                                  updateExercise(index, { weight: parseFloat(value) || 0 });
                                }
                              }}
                              className={`flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                isAutoCalculated 
                                  ? 'border-green-300 bg-green-50' 
                                  : 'border-gray-300'
                              }`}
                              placeholder="Enter weight"
                              step="0.5"
                            />
                            
                            {suggestedWeight && !isAutoCalculated && (
                              <button
                                onClick={() => updateExercise(index, { weight: suggestedWeight })}
                                className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                title={`Use calculated weight: ${suggestedWeight} lbs`}
                              >
                                Auto
                              </button>
                            )}
                          </div>
                          
                          {suggestedWeight && !isAutoCalculated && (
                            <div className="mt-2 text-sm text-gray-600">
                              Suggested: {suggestedWeight} lbs (calculated from your {formatUniversalWeightType(exerciseData!.universalWeightType!)})
                            </div>
                          )}
                        </div>
                        ) : (
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="flex items-center space-x-2">
                            <span className="text-orange-600">🔍</span>
                            <div>
                              <div className="text-sm font-medium text-orange-900">
                                Discovery Mode Active
                              </div>
                              <div className="text-xs text-orange-700">
                                You'll find the right weight during your workout for {finalSetReps} reps
                              </div>
                            </div>
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  })()}
                </div>

                <SetEditor
                  sets={exercise.sets}
                  onChange={(sets) => updateExercise(index, { sets })}
                />
              </div>
            ))}
          </div>
        )}
        
        {/* Add Exercise Button - moved to bottom for better UX */}
        <div className="mt-4">
          <button
            onClick={() => setShowExerciseSelector(true)}
            className="w-full bg-blue-600 text-white py-2 rounded text-sm font-medium cursor-pointer hover:bg-blue-700"
            style={{ pointerEvents: 'auto' }}
          >
            Add Exercise
          </button>
        </div>
      </div>

      <div className="flex space-x-3 relative z-10">
        <button
          onClick={handleSave}
          className="flex-1 bg-blue-600 text-white py-2 rounded cursor-pointer hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={!name.trim()}
          style={{ pointerEvents: 'auto' }}
        >
          Save Training Day
        </button>
        <button
          onClick={() => navigate('/training-days')}
          className="flex-1 bg-gray-300 text-gray-700 py-2 rounded cursor-pointer hover:bg-gray-400"
          style={{ pointerEvents: 'auto' }}
        >
          Cancel
        </button>
      </div>

      {showExerciseSelector && (
        <ExerciseSelector
          onSelect={addExercise}
          onClose={() => setShowExerciseSelector(false)}
          excludeIds={excludeIds}
        />
      )}
      
      {/* Universal Weight Modal */}
      {showUniversalWeightModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              How do you want to set weights for {showUniversalWeightModal.exerciseName}?
            </h3>
            <div className="mb-4">
              <div className="bg-blue-50 p-3 rounded-lg mb-3">
                <h4 className="text-sm font-medium text-blue-900 mb-1">Option 1: Set a baseline (recommended if you know your strength)</h4>
                <p className="text-xs text-blue-700">We'll automatically calculate perfect weights for any rep range</p>
              </div>
              <div className="bg-orange-50 p-3 rounded-lg">
                <h4 className="text-sm font-medium text-orange-900 mb-1">Option 2: Find your weight during the workout</h4>
                <p className="text-xs text-orange-700">Perfect for new exercises - we'll guide you to find the right weight</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight Type
                </label>
                <select
                  value={universalWeightType}
                  onChange={(e) => setUniversalWeightType(e.target.value as UniversalWeightType)}
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="5RM">5 Rep Max (safest to test)</option>
                  <option value="5RM_1RIR">5 Reps @ 1 RIR (most sustainable)</option>
                  <option value="1RM">1 Rep Max (gold standard)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Weight (lbs)
                </label>
                <input
                  type="number"
                  value={universalWeightInput || ''}
                  onChange={(e) => setUniversalWeightInput(parseFloat(e.target.value) || 0)}
                  className="w-full p-2 border border-gray-300 rounded"
                  placeholder="Enter weight"
                  step="0.5"
                  autoFocus
                />
              </div>
              
              {universalWeightInput > 0 && (
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-gray-700">
                    For {showUniversalWeightModal.targetReps} reps: ~{getSuggestedWeight(universalWeightInput, universalWeightType, showUniversalWeightModal.targetReps)} lbs
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={saveUniversalWeight}
                disabled={universalWeightInput <= 0}
                className="flex-1 bg-blue-600 text-white py-2 rounded disabled:bg-gray-300"
              >
                Save Baseline
              </button>
              <button
                onClick={() => setShowUniversalWeightModal(null)}
                className="flex-1 bg-orange-600 text-white py-2 rounded"
              >
                Find During Workout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingDayForm;